<template>
    <div class="flex flex-col items-center min-h-screen text-black  bg-green-200">
        <div class="flex flex-col items-center mt-20 mb-50">
            <Logo/>
        </div>
        <div class="flex flex-col items-center px-4 mb-110 mt-40 w-full max-w-sm gap-4">
            <div class="flex items-center gap-2 relative cursor-pointer md:scale-125"
                 @click="selectCountry(suggestedCountry.path)">
                <img :src="countrySuggestedBgShadow" class="absolute -left-7 object-cover -z-10 max-w-[110%]">
                <img :src="locationPin" class="mr-10 w-[45px] relative z-10">
                <CountrySuggestedButton v-if="suggestedCountry"
                                        :country-name="suggestedCountry.name"
                                        :flag-url="suggestedCountry.flagUrl || undefined"
                                        :href="suggestedCountry.path"
                                        class="relative z-10 hover:text-pink-100"/>
            </div>
        </div>
        <h3 class="-mt-30 mb-40 xs:block md:hidden lg:hidden text-lg">
            {{ $translate('CountrySelector.AllCountries') }}
        </h3>
        <div class="flex flex-wrap justify-center px-4 w-full gap-4 country-grid">
            <div v-for="country in countries"
                 :key="country.cultureCode"
                 class="country-btn-wrapper">
                <CountryButton :country-name="country.name"
                               :flag-url="country.flagUrl || undefined"
                               :uniqueman-url="country.uniquemanUrl || undefined"
                               :href="country.path"
                               class="country-btn-size hover:text-pink-100"
                               @click="selectCountry(country.path)"/>
                <div v-if="country.speechBubble"
                     class="font-fauli speech-bubble">
                    <span class="speech-bubble-text">{{ country.speechBubble }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import CountryButton from './CountryButton.vue';
import CountrySuggestedButton from './CountrySuggestedButton.vue';
import Logo from '@/assets/images/Logo.vue';
import contentApi from '@/project/content/api/contentApi';
import { CountryItem } from '@/api/content/models';
import { setSelectedCountry } from './countrySelector.utils';
import countrySuggestedBgShadow from '@/assets/images/country-suggested-bg-shadow.svg';
import locationPin from '@/assets/images/location-pin.svg';

const FALLBACK_PATH = '/en';

const langVariantMap: Record<string, string> = {
    'nb': 'no',
    'nn': 'no',
};

function isValidCountryData(countries: CountryItem[] | null | undefined): countries is CountryItem[] {
    return Array.isArray(countries) && countries.length > 0 && countries.every(c => c.cultureCode && c.path);
}

function buildCountryMap(countries: CountryItem[]): Record<string, CountryItem> {
    const map: Record<string, CountryItem> = {};
    for (const country of countries) {
        const cultureCode = country.cultureCode?.toLowerCase();
        if (!cultureCode) continue;
        const langCode = cultureCode.split('-')[0];
        const mappedLang = langVariantMap[langCode] || langCode;
        map[cultureCode] = country;
        if (!map[mappedLang]) {
            map[mappedLang] = country;
        }
    }
    return map;
}

function getSuggestedCountry(countries: CountryItem[]): CountryItem | null {
    if (!isValidCountryData(countries)) return null;
    const countryMap = buildCountryMap(countries);
    const browserLang = navigator.language?.toLowerCase() || 'en';
    const browserLangShort = browserLang.split('-')[0];
    const mappedLang = langVariantMap[browserLangShort] || browserLangShort;
    const defaultCountry = countryMap['en'] || countries[0];
    return countryMap[browserLang] || countryMap[mappedLang] || defaultCountry;
}

export default defineComponent({
    name: 'CountrySelector',
    components: {
        CountryButton,
        CountrySuggestedButton,
        Logo,
    },
    setup() {
        const countries = ref<CountryItem[]>([]);
        const suggestedCountry = ref<CountryItem | null>(null);

        function fallbackToDefault() {
            setSelectedCountry(FALLBACK_PATH);
            window.location.assign(FALLBACK_PATH);
        }

        onMounted(async() => {
            try {
                const siteSettings = await contentApi.settings;
                if (!isValidCountryData(siteSettings?.countries)) {
                    fallbackToDefault();
                    return;
                }
                countries.value = siteSettings.countries;
                suggestedCountry.value = getSuggestedCountry(siteSettings.countries);
            } catch {
                fallbackToDefault();
            }
        });

        function selectCountry(path: string) {
            if (!path) {
                fallbackToDefault();
                return;
            }
            setSelectedCountry(path);
            window.location.assign(path);
        }

        return {
            selectCountry,
            suggestedCountry,
            countries,
            countrySuggestedBgShadow,
            locationPin,
        };
    },
});
</script>

<style scoped>
    .country-btn-size {
        width: 280px;
        height: 56px;
    }

    .country-btn-wrapper {
        position: relative;
        margin-bottom: 60px;
    }

    .speech-bubble {
        position: absolute;
        left: 10%;
        bottom: 70%;
        margin-top: 10px;
        width: 170px;
        height: 104px;
        display: none;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 10;
    }

    .speech-bubble::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url('@/assets/images/speech-bubble.svg');
        background-size: 100% 100%;
        background-repeat: no-repeat;
        background-position: center;
        transform: scaleX(-1);
        z-index: -1;
    }

    .speech-bubble-text {
        font-size: 12px;
        font-weight: normal;
        text-align: center;
        color: #000;
        line-height: 1.3;
        padding: 0 20px 0 20px;
        margin-top: -15px
    }

    @screen md {
        .country-btn-size {
            width: 280px;
        }

        .country-grid {
            max-width: calc(280px * 3 + 1rem * 2);
        }
    }

    @screen lg {
        .country-btn-wrapper:hover .speech-bubble {
            display: flex;
        }
    }
</style>
